import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lightbulb, RefreshCw, Wallet, Target, Calendar, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import useTips from '../hooks/useTips';
import { API_URL } from '../config/api';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

// Mapping des modules vers leurs icônes et labels
const MODULE_CONFIG = {
  portefeuille: {
    icon: Wallet,
    label: 'Portefeuilles',
    color: 'text-primary',
    bgColor: 'bg-primary/10'
  },
  objectifs: {
    icon: Target,
    label: 'Objectifs',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100'
  },
  calendrier: {
    icon: Calendar,
    label: 'Calendrier',
    color: 'text-accent',
    bgColor: 'bg-accent/20'
  }
};

/**
 * Composant AstucesTab - Rubrique centrale pour toutes les astuces
 */
const AstucesTab = () => {
  const { dismissedTips, loading, loadDismissedTips, restoreTip } = useTips();
  const [expandedModule, setExpandedModule] = useState(null);
  const [restoring, setRestoring] = useState(null);

  useEffect(() => {
    loadDismissedTips();
  }, [loadDismissedTips]);

  const handleRestore = async (tipId) => {
    setRestoring(tipId);
    const success = await restoreTip(tipId);
    if (success) {
      toast.success('Astuce réactivée');
    } else {
      toast.error('Erreur lors de la réactivation');
    }
    setRestoring(null);
  };

  const toggleModule = (module) => {
    setExpandedModule(expandedModule === module ? null : module);
  };

  const totalDismissed = Object.values(dismissedTips).reduce(
    (acc, tips) => acc + tips.length, 
    0
  );

  const hasNoTips = totalDismissed === 0 && !loading;

  return (
    <div className="space-y-5">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
          <Lightbulb className="h-7 w-7 text-primary" strokeWidth={1.5} />
        </div>
        <h2 className="text-lg font-semibold text-foreground tracking-tight">
          Tes astuces
        </h2>
        <p className="text-xs text-muted-foreground mt-1">
          Retrouve ici les conseils que tu as masqués
        </p>
      </motion.div>

      {/* Loading state */}
      {loading && (
        <div className="flex justify-center py-8">
          <motion.div
            className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          />
        </div>
      )}

      {/* Empty state */}
      {hasNoTips && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="premium-card text-center py-10"
        >
          <div className="text-4xl mb-3 opacity-60">💡</div>
          <h3 className="text-base font-medium text-foreground mb-1">
            Aucune astuce masquée
          </h3>
          <p className="text-xs text-muted-foreground max-w-xs mx-auto">
            Les conseils que tu masques dans l'application apparaîtront ici pour que tu puisses les retrouver facilement.
          </p>
        </motion.div>
      )}

      {/* Tips by module */}
      {!loading && totalDismissed > 0 && (
        <div className="space-y-3">
          {Object.entries(dismissedTips).map(([module, tips]) => {
            if (!tips || tips.length === 0) return null;
            
            const config = MODULE_CONFIG[module] || {
              icon: Lightbulb,
              label: module,
              color: 'text-muted-foreground',
              bgColor: 'bg-muted'
            };
            const Icon = config.icon;
            const isExpanded = expandedModule === module;

            return (
              <motion.div
                key={module}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="premium-card !p-0 overflow-hidden"
              >
                {/* Module header - Clickable */}
                <button
                  onClick={() => toggleModule(module)}
                  className="w-full p-4 flex items-center justify-between hover:bg-muted/30 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 ${config.bgColor} rounded-xl flex items-center justify-center`}>
                      <Icon className={`h-4.5 w-4.5 ${config.color}`} strokeWidth={1.5} />
                    </div>
                    <div className="text-left">
                      <h3 className="text-sm font-medium text-foreground">
                        {config.label}
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        {tips.length} astuce{tips.length > 1 ? 's' : ''} masquée{tips.length > 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <motion.div
                    animate={{ rotate: isExpanded ? 90 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronRight className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
                  </motion.div>
                </button>

                {/* Tips list */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="border-t border-border/50 p-4 space-y-3">
                        {tips.map((tip) => (
                          <motion.div
                            key={tip.id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="bg-muted/30 rounded-xl p-3"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-medium text-foreground mb-1">
                                  {tip.title}
                                </h4>
                                <p className="text-xs text-muted-foreground leading-relaxed">
                                  {tip.content}
                                </p>
                                {tip.dismissed_at && (
                                  <p className="text-[10px] text-muted-foreground/60 mt-2">
                                    Masquée le {new Date(tip.dismissed_at).toLocaleDateString('fr-FR')}
                                  </p>
                                )}
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleRestore(tip.id)}
                                disabled={restoring === tip.id}
                                className="h-8 px-3 text-xs text-primary hover:text-primary hover:bg-primary/10 flex-shrink-0"
                              >
                                {restoring === tip.id ? (
                                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  'Réactiver'
                                )}
                              </Button>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Info footer */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-center text-[11px] text-muted-foreground/70 px-4"
      >
        Les astuces réactivées réapparaîtront dans leur module d'origine
      </motion.p>
    </div>
  );
};

export default AstucesTab;
